import { CanceledError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAlertView } from '~/app';
import { getActivationPreview } from '~/features/employer/billing/api';
import type { ActivationPreview, FeeAuthorization } from '~/features/employer/billing/types';
import { useManagePaymentMethod } from '~/features/employer/billing/useManagePaymentMethod';
import { withRequestDeadline } from '~/utils/requestDeadline';
import { MARKET_READ_TIMEOUT_MS } from './requestPolicy';

type Params<TPayload> = {
  /**
   * The response the user is looking at. A preview or dialog for any other response is dropped, so
   * Accept can never confirm the fee of one response and accept another. Leave it out where one
   * response is on screen for the life of the hook (the negotiation modal).
   */
  selectedResponseId?: number | null;
  /** Runs the accept, with the fee authorization when the dialog was shown. */
  onAccept: (payload: TPayload, feeAuthorization?: FeeAuthorization) => void;
};

/**
 * [MCV-1672] [MCV-1889] An Accept on a B2B response previews the commitment first and only
 * opens the shared ActivationFeeDialog when this viewer is the one billed (the buyer). The same
 * steps run from the Received list, the Sent list and the negotiation modal. Modelled on
 * service-opportunities/useServiceAcceptFee.
 *
 * A refused fee schedule re-enters through `requestAccept(..., notice)`; with a notice the accept
 * never falls through to a bare accept, because the server would refuse it again and the employer
 * has not seen a schedule to authorize.
 */
export const useB2BAcceptFee = <TPayload = void>({
  selectedResponseId,
  onAccept,
}: Params<TPayload>) => {
  const alertView = useAlertView();
  const alertViewRef = useRef(alertView);
  alertViewRef.current = alertView;
  const handleManagePaymentMethod = useManagePaymentMethod();
  const [dialog, setDialog] = useState<{
    responseId: number;
    payload: TPayload;
    preview: ActivationPreview;
    notice: string | null;
  } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  // The in-flight preview. A stalled read must not pin the controls: it is bounded by
  // MARKET_READ_TIMEOUT_MS and aborted when the selection changes or the owner unmounts.
  const abortRef = useRef<AbortController | null>(null);
  const previewResponseIdRef = useRef<number | null>(null);
  const mountedRef = useRef(false);
  const selectedResponseIdRef = useRef(selectedResponseId);
  selectedResponseIdRef.current = selectedResponseId;
  const onAcceptRef = useRef(onAccept);
  onAcceptRef.current = onAccept;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (selectedResponseId === undefined) return;
    // Abort only a preview for another response. This effect can run after an Accept click that
    // landed right after auto-select, and aborting then would cancel that click's own preview.
    if (previewResponseIdRef.current !== selectedResponseId) abortRef.current?.abort();
  }, [selectedResponseId]);

  // Changing the selection under an open dialog invalidates it: Confirm would accept the wrong one.
  useEffect(() => {
    if (selectedResponseId !== undefined && dialog && dialog.responseId !== selectedResponseId)
      setDialog(null);
  }, [dialog, selectedResponseId]);

  const isStale = useCallback(
    (responseId: number) =>
      selectedResponseIdRef.current !== undefined && selectedResponseIdRef.current !== responseId,
    []
  );

  const requestAccept = useCallback(
    async (responseId: number, payload: TPayload, notice: string | null = null) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      previewResponseIdRef.current = responseId;
      setPreviewing(true);
      let preview: ActivationPreview;
      try {
        preview = await withRequestDeadline(
          (signal) => getActivationPreview(responseId, signal),
          MARKET_READ_TIMEOUT_MS,
          controller.signal
        );
      } catch (err) {
        if (!mountedRef.current) return;
        if (abortRef.current === controller) setPreviewing(false);
        // Aborted by a selection change or unmount: nothing to report.
        if (err instanceof CanceledError) return;
        console.error('Failed to preview activation billing', err);
        // Nothing may be accepted without the employer having seen the fee.
        alertViewRef.current.send({
          type: 'ON_SHOW_ALERT',
          payload: {
            text: 'Could not preview the commitment',
            subText: 'Please try accepting the offer again.',
            type: 'error',
            timeout: 5000,
          },
        });
        return;
      }
      if (!mountedRef.current) return;
      setPreviewing(false);
      // The selection moved while the preview was in flight: this preview is for a response the
      // user is no longer looking at, so drop it rather than show A's fee and accept B.
      if (isStale(responseId)) return;
      if (preview.isBillable && preview.isCurrentUserPayer) {
        setDialog({ responseId, payload, preview, notice });
        return;
      }
      // A refused fee authorization never falls through to a bare accept.
      if (notice != null) return;
      onAcceptRef.current(payload);
    },
    [isStale]
  );

  const close = useCallback(() => setDialog(null), []);
  const confirm = useCallback(
    (feeAuthorization?: FeeAuthorization) => {
      if (!dialog) return;
      const { responseId, payload } = dialog;
      setDialog(null);
      if (!isStale(responseId)) onAcceptRef.current(payload, feeAuthorization);
    },
    [dialog, isStale]
  );

  return {
    requestAccept,
    previewing,
    dialogProps: {
      open: dialog != null,
      preview: dialog?.preview ?? null,
      notice: dialog?.notice ?? null,
      onClose: close,
      onConfirm: confirm,
      onManagePaymentMethod: handleManagePaymentMethod,
    },
  };
};
